/**
 * Sport-specific terminology mapping.
 *
 * Single source of truth for the labels the UI uses to describe
 * scoring events, rule infractions, and player swaps across sports.
 *
 * Keep this file the *only* place that maps sport identity -> display
 * string. Callers should never branch on `isHockey` / `isSoccer`
 * themselves — read from the terminology object instead.
 */

export interface SportTerminology {
  /** Scoring event label — e.g. "Goal" */
  scoringEvent: string
  /** Rule infraction label — e.g. "Penalty" (hockey) or "Foul" (soccer, generic) */
  infraction: string
  /** Player substitution label — e.g. "Sub" */
  substitution: string
}

interface GetSportTerminologyArgs {
  /** Canonical sport name from the sport record — 'hockey' | 'soccer' | 'generic' | null */
  sportName?: string | null
  /** Free-text generic sport name (e.g. "basketball", "lacrosse") when sport is generic */
  genericSportName?: string | null
}

const DEFAULT_TERMINOLOGY: SportTerminology = {
  scoringEvent: 'Point',
  infraction: 'Foul',
  substitution: 'Sub',
}

const HOCKEY_TERMINOLOGY: SportTerminology = {
  scoringEvent: 'Goal',
  infraction: 'Penalty',
  substitution: 'Sub',
}

const SOCCER_TERMINOLOGY: SportTerminology = {
  scoringEvent: 'Goal',
  infraction: 'Foul',
  substitution: 'Sub',
}

/**
 * Returns display terminology for a given sport.
 *
 * Hockey and soccer use "Goal" as the scoring event; all other sports use
 * "Point". Hockey uses "Penalty" for infractions; everything else uses "Foul".
 * Unknown / missing sports fall back to the default (Point / Foul / Sub).
 *
 * @param args.sportName - canonical sport name ('hockey', 'soccer', 'generic')
 * @param args.genericSportName - free-text generic sport name, reserved for
 *   future per-generic-sport overrides; currently unused for mapping
 */
export const getSportTerminology = ({
  sportName,
}: GetSportTerminologyArgs): SportTerminology => {
  switch (sportName) {
    case 'hockey':
      return HOCKEY_TERMINOLOGY
    case 'soccer':
      return SOCCER_TERMINOLOGY
    default:
      return DEFAULT_TERMINOLOGY
  }
}
